import { useState, useMemo } from "react"
import { Menu, X, Home, ShoppingCart, Receipt, Banknote, Settings, Package, Hotel, CreditCard, Wallet, ShoppingBag, Building2, FileText, BookOpen, Building, UserCog } from "lucide-react"
import { Button } from "../components/ui/button"
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet"
import { cn } from "../lib/utils"
import { useAuth } from "../contexts/auth-context"
import { Link, useLocation } from "react-router-dom"

interface NavigationItem {
  name: string
  icon: any
  href: string
  adminOnly?: boolean
  roles?: string[]
}

// Standalone navigation items (matching sidebar)
const standaloneItems: NavigationItem[] = [
  { 
    name: "Dashboard", 
    icon: Home, 
    href: "/dashboard"
  },
  { 
    name: "Banking & Cash", 
    icon: Banknote, 
    href: "/dashboard/banking"
  },
]

// Navigation groups (matching sidebar structure)
const navigationGroups = [
  {
    name: "Sales & Revenue",
    items: [
      { 
        name: "Sales & Invoicing", 
        icon: ShoppingCart, 
        href: "/dashboard/sales"
      },
      { 
        name: "Point of Sale", 
        icon: CreditCard, 
        href: "/dashboard/pos"
      },
    ]
  },
  {
    name: "Purchases & Expenses",
    items: [
      { 
        name: "Expenses & Purchases", 
        icon: Receipt, 
        href: "/dashboard/expenses"
      },
      { 
        name: "Accounts Payable", 
        icon: Wallet, 
        href: "/dashboard/enhanced-accounts-payable"
      },
      { 
        name: "Purchase Orders", 
        icon: ShoppingBag, 
        href: "/dashboard/purchase-orders"
      },
      { 
        name: "Supplier Portal", 
        icon: Building2, 
        href: "/dashboard/vendors"
      },
    ]
  },
  {
    name: "Operations",
    items: [
      { 
        name: "Inventory & Products", 
        icon: Package, 
        href: "/dashboard/inventory"
      },
      { 
        name: "Hotel Management", 
        icon: Hotel, 
        href: "/dashboard/hotel"
      },
    ]
  },
  {
    name: "Accounting",
    items: [
      { 
        name: "Accounting", 
        icon: FileText, 
        href: "/dashboard/accounting"
      },
      { 
        name: "Journal Entries Hub", 
        icon: BookOpen, 
        href: "/dashboard/journal-hub"
      },
    ]
  },
  {
    name: "Assets & HR",
    items: [
      { 
        name: "Fixed Assets", 
        icon: Building, 
        href: "/dashboard/fixed-assets"
      },
      { 
        name: "Payroll Management", 
        icon: UserCog, 
        href: "/dashboard/payroll"
      },
    ]
  },
  {
    name: "Settings",
    items: [
      { 
        name: "Company", 
        icon: Building2, 
        href: "/dashboard/companies"
      },
      { 
        name: "Users", 
        icon: UserCog, 
        href: "/dashboard/users",
        adminOnly: true
      },
    ]
  },
]

export function MobileNavigation() {
  const [open, setOpen] = useState(false)
  const { user } = useAuth()
  const location = useLocation()

  // Build navigation structure matching sidebar logic
  const navigationStructure = useMemo(() => {
    // Waiter / receptionist: only Hotel Management
    if (user?.role === "waiter" || user?.role === "receptionist") {
      return [
        {
          type: 'item' as const,
          item: {
            name: "Hotel Management",
            icon: Hotel,
            href: "/dashboard/hotel",
          }
        }
      ]
    }

    // For other roles, build structure with groups
    const structure: Array<{ type: 'item', item: NavigationItem } | { type: 'group', name: string, items: NavigationItem[] }> = []
    
    // Add standalone items first
    standaloneItems.forEach(item => {
      // Filter based on role restrictions
      if (item.adminOnly && user?.role !== 'super_admin' && user?.role !== 'admin') return
      if (item.roles && user?.role && !item.roles.includes(user.role)) return
      structure.push({ type: 'item', item })
    })

    // Add groups with their items
    navigationGroups.forEach(group => {
      const filteredItems = group.items.filter(item => {
        if (item.adminOnly && user?.role !== 'super_admin' && user?.role !== 'admin') return false
        if (item.roles && user?.role && !item.roles.includes(user.role)) return false
        return true
      })
      
      if (filteredItems.length > 0) {
        structure.push({ type: 'group', name: group.name, items: filteredItems })
      }
    })

    return structure
  }, [user?.role])

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="lg:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Open navigation menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-80 p-0">
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 flex items-center justify-center">
                  <img 
                    src="/brand-e.svg" 
                    alt="Monsterado Lounge" 
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-foreground">Monsterado Lounge</h2>
                  <p className="text-xs text-muted-foreground">Hotel Management</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto p-4">
            <div className="space-y-4">
              {navigationStructure.map((section, idx) => {
                if (section.type === 'item') {
                  const item = section.item
                  const isActive =
                    location.pathname === item.href ||
                    (item.href !== "/dashboard" &&
                      location.pathname.startsWith(item.href + "/"))

                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={cn(
                        "flex items-center gap-3 px-3 py-3 text-sm rounded-lg transition-colors",
                        isActive
                          ? "bg-primary text-primary-foreground font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground",
                      )}
                      onClick={() => setOpen(false)}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.name}
                    </Link>
                  )
                } else {
                  // Group with label
                  return (
                    <div key={section.name} className="space-y-2">
                      <div className="px-3 py-1.5">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          {section.name}
                        </p>
                      </div>
                      {section.items.map((item) => {
                        const isActive =
                          location.pathname === item.href ||
                          (item.href !== "/dashboard" &&
                            location.pathname.startsWith(item.href + "/"))

                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={cn(
                              "flex items-center gap-3 px-3 py-3 text-sm rounded-lg transition-colors",
                              isActive
                                ? "bg-primary text-primary-foreground font-medium"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground",
                            )}
                            onClick={() => setOpen(false)}
                          >
                            <item.icon className="w-5 h-5" />
                            {item.name}
                          </Link>
                        )
                      })}
                    </div>
                  )
                }
              })}
            </div>
          </nav>

          <div className="p-4 border-t border-border">
            <div className="bg-card p-3 rounded-lg border border-border">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center">
                  <span className="text-xs font-medium">
                    {(() => {
                      const fullName = (user as any)?.fullName || (user as any)?.name
                      if (fullName) {
                        const parts = fullName.trim().split(' ')
                        if (parts.length >= 2) {
                          return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
                        }
                        return fullName.charAt(0).toUpperCase()
                      }
                      if (user?.firstName && user?.lastName) {
                        return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
                      }
                      if (user?.firstName) {
                        return user.firstName.charAt(0).toUpperCase()
                      }
                      if (user?.email) {
                        return user.email.charAt(0).toUpperCase()
                      }
                      return 'U'
                    })()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {(user as any)?.fullName || (user as any)?.name || 
                     (user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null) ||
                     user?.firstName ||
                     (user?.email ? user.email.split('@')[0] : 'User')}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.role
                      ? (user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : user.role.charAt(0).toUpperCase() + user.role.slice(1))
                      : 'Employee'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
